import {
  type CSSProperties,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { groupBy } from 'lodash'
import { DropdownItem } from 'shared/ui/Dropdown/ui/DropdownItem'
import { type ITextFieldProps } from 'shared/ui/TextField'
import { getAriaLabel } from 'shared/lib'
import {
  DropdownGroup,
  Scrollbar,
  SpinnerLoader,
  type IDropdownItem,
  dropDownStore,
  type IDropdownGroupProps,
} from 'shared/ui'
import { IS_APP_LIGHT } from 'shared/config'
import styles from './styles.module.scss'
import { DropdownTextInput } from '../DropdownTextInput'
import { useDropdownKeyboardNavigation } from './useDropdownKeyboardNavigation'

export type IDropdownListProps = {
  items: IDropdownItem[]
  parent?: IDropdownItem
  width?: CSSProperties['width']
  maxHeight?: number
  withSearch?: boolean
  onSearch?: (value: string) => void
  search?: string
  withTextInput?: boolean
  topListComponent?: ReactNode
  underControlComponent?: ReactNode
  bottomActions?: ReactNode
  bottomActionsStyle?: CSSProperties
  onChange?: (item: IDropdownItem, child?: IDropdownItem) => void
  onPressEnter?: (text: string) => void
  textFieldProps?: ITextFieldProps
  childContainerWidth?: number
  onLoadMore?: () => void
  loading?: boolean
  loadingMore?: boolean
  searchWrapClassName?: string
  isTopReverse?: boolean
  placeholder?: string
  className?: string
  notFoundText?: ReactNode
  labelItemsContent?: ReactNode
  withGrouping?: boolean
  dropdownGroupProps?: Partial<IDropdownGroupProps>
  dropdownId?: string
  allHideOnScroll?: boolean
  withDeselectAll?: boolean
  onSearchChange?: (value: string) => void
  autoHeightMax?: number | string
  serverSideSearch?: boolean
  onClose?: () => void
  minItemWidth?: number
}
export const DropdownList = ({
  items,
  onChange,
  width,
  withSearch,
  withTextInput,
  bottomActions,
  bottomActionsStyle,
  parent,
  maxHeight,
  textFieldProps,
  topListComponent,
  underControlComponent,
  childContainerWidth,
  onSearch,
  search,
  onPressEnter = () => {},
  onLoadMore,
  loading,
  loadingMore,
  searchWrapClassName,
  isTopReverse,
  placeholder = 'Select',
  className,
  notFoundText = 'No results found',
  labelItemsContent,
  withGrouping,
  allHideOnScroll,
  dropdownId,
  dropdownGroupProps,
  withDeselectAll = false,
  onSearchChange,
  autoHeightMax,
  serverSideSearch = false,
  onClose,
  minItemWidth = 115,
}: IDropdownListProps) => {
  const refList = useRef<HTMLDivElement>(null)
  const [searchLocal, setSearchLocal] = useState('')

  const filterItems = useMemo(() => {
    if (onSearch !== undefined && serverSideSearch) return items
    if (!searchLocal) return items

    const search = searchLocal.trim().toLowerCase()

    return items.filter((item) =>
      item.searchPattern
        ? item.searchPattern.toLowerCase().includes(search)
        : item.label.toLowerCase().includes(search)
    )
  }, [items, searchLocal, onSearch, serverSideSearch])

  const dropdownItemsByGroup = dropdownGroupProps?.dropdownItemsByGroup
  const groupItems = useMemo(() => {
    if (!withGrouping) return undefined

    if (withDeselectAll && dropdownItemsByGroup) {
      const currentVisibleGroups = groupBy(filterItems, 'group')

      return dropdownItemsByGroup.map<[string, IDropdownItem[]]>(([groupName]) => {
        const visibleItems = currentVisibleGroups[groupName] || []
        return [groupName, visibleItems]
      })
    }

    return Object.entries<IDropdownItem[]>(groupBy(filterItems, 'group'))
  }, [withGrouping, withDeselectAll, filterItems, dropdownItemsByGroup])

  const isAllItemsSelectedInGroups = groupItems
    ? groupItems.every(([, arr]) => Array.isArray(arr) && arr.length === 0)
    : false

  const flatItems = useMemo(() => {
    if (groupItems) {
      return groupItems.flatMap(([, groupItemsList]) => groupItemsList)
    }
    return filterItems
  }, [groupItems, filterItems])

  const isSelecting = useRef(false)

  const handleListChange = useCallback(
    (item: IDropdownItem, child?: IDropdownItem) => {
      isSelecting.current = true
      onChange?.(item, child)
      queueMicrotask(() => {
        isSelecting.current = false
      })
    },
    [onChange]
  )

  const { highlightedId, expandedId, handleKeyDown, resetHighlight, collapseExpanded } =
    useDropdownKeyboardNavigation({
      flatItems,
      onChange: handleListChange,
      parent,
      onClose,
    })

  const contentRef = useRef<HTMLDivElement>(null)
  const hasTextInput = withSearch || withTextInput

  useEffect(() => {
    if (!hasTextInput) {
      contentRef.current?.focus()
    }
  }, [])

  useEffect(() => {
    resetHighlight()
  }, [filterItems])

  useEffect(() => {
    if (highlightedId !== null) {
      contentRef.current
        ?.querySelector(`[data-dropdown-id="${highlightedId}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedId])

  const handleMouseMove = () => {
    if (highlightedId !== null) {
      resetHighlight()
    }
  }

  const handleSubmenuCollapse = () => {
    collapseExpanded()
    if (isSelecting.current) return
    requestAnimationFrame(() => {
      const input = contentRef.current?.querySelector<HTMLInputElement>('input')
      if (input) {
        input.focus()
      } else {
        contentRef.current?.focus()
      }
    })
  }

  const isSelect = useMemo(() => {
    return !!items.find((item) => item.activeValue)
  }, [items])

  useEffect(() => {
    if (refList.current && !IS_APP_LIGHT) {
      refList.current.querySelector('[data-selected="true"]')?.scrollIntoView()
    }
  }, [])
  const [text, setText] = useState('')

  const onInputPressEnter = () => {
    onPressEnter(text)
  }

  const handleSearch = (value: string) => {
    onSearchChange?.(value)

    if (onSearch) {
      onSearch(value)
    } else {
      setSearchLocal(value)
    }
  }

  useEffect(() => {
    setSearchLocal(search || '')
  }, [search])

  const handleScroll = (e: UIEvent) => {
    if (allHideOnScroll) {
      dropDownStore.allHide(dropdownId)
    }
    if (onLoadMore) {
      const target = e.target as HTMLDivElement
      const scrollHeight = target.scrollHeight
      const scrollTop = target.scrollTop
      const offsetHeight = target.offsetHeight

      if (scrollHeight <= scrollTop + offsetHeight + 100) {
        onLoadMore()
      }
    }
  }
  return (
    <div className={classNames(styles.wrap, className)} aria-label={getAriaLabel('DropdownList')}>
      <div
        ref={contentRef}
        className={classNames(styles.content, {
          'top-dropdown-column-reverse-content': isTopReverse,
        })}
        onKeyDown={!hasTextInput ? handleKeyDown : undefined}
        tabIndex={!hasTextInput ? 0 : undefined}
      >
        {topListComponent}
        {withSearch && (
          <DropdownTextInput
            dropdownTextInputClassName={searchWrapClassName}
            textFieldProps={{
              value: search || searchLocal,
              onChange: handleSearch,
              ...textFieldProps,
              InputProps: {
                placeholder,
                ...textFieldProps?.InputProps,
                onKeyDown: handleKeyDown,
              },
            }}
          />
        )}
        {withTextInput && (
          <DropdownTextInput
            textFieldProps={{
              value: text,
              onChange: setText,
              ...textFieldProps,
              InputProps: {
                ...textFieldProps?.InputProps,
                onKeyDown: handleKeyDown,
              },
            }}
            onPressEnter={onInputPressEnter}
          />
        )}
        {underControlComponent}

        {loading ? (
          <SpinnerLoader ariaLabel={'DropdownList'} style={{ margin: 12 }} size={24} />
        ) : (
          <>
            {groupItems && (
              <Scrollbar
                autoHeight
                autoHeightMin={0}
                autoHeightMax={autoHeightMax || 300}
                onScroll={handleScroll}
                aria-label={getAriaLabel('DropdownList', 'items')}
              >
                <div onMouseMove={handleMouseMove}>
                  {groupItems.map(([group, groupItemsList]) => (
                    <DropdownGroup
                      key={group}
                      title={group === 'undefined' ? '' : group}
                      items={groupItemsList}
                      onChange={handleListChange}
                      {...dropdownGroupProps}
                      keyboardNav={{
                        highlightedId,
                        expandedId,
                        onCollapse: handleSubmenuCollapse,
                        parentsDropdownId: dropdownId ? [dropdownId] : undefined,
                      }}
                    />
                  ))}
                </div>
                {loadingMore && (
                  <SpinnerLoader ariaLabel={'DropdownList'} style={{ margin: 12 }} size={24} />
                )}
              </Scrollbar>
            )}
            {!groupItems && Boolean(filterItems.length) && (
              <div className={styles.wrapItems} ref={refList}>
                <Scrollbar
                  autoHeight
                  autoHeightMin={0}
                  autoHeightMax={maxHeight ?? 300}
                  onScroll={handleScroll}
                  aria-label={getAriaLabel('DropdownList', 'items')}
                >
                  <div
                    className={styles.items}
                    style={{
                      maxWidth: width ? 'unset' : undefined,
                      minWidth: minItemWidth,
                    }}
                    onMouseMove={handleMouseMove}
                  >
                    {labelItemsContent}
                    {filterItems.map((item) => {
                      return (
                        <div
                          key={item.id}
                          data-selected={item.activeValue}
                          data-dropdown-id={item.id}
                          aria-label={getAriaLabel(
                            item.ariaLabel ? `${item.ariaLabel}List` : 'DropdownList',
                            'item'
                          )}
                        >
                          <DropdownItem
                            item={item}
                            onChange={handleListChange}
                            parent={parent}
                            isSelect={isSelect}
                            keyboardNav={{
                              isHighlighted: item.id === highlightedId,
                              isExpanded: item.id === expandedId,
                              onCollapse: handleSubmenuCollapse,
                              parentsDropdownId: dropdownId ? [dropdownId] : undefined,
                            }}
                            childContainerWidth={childContainerWidth}
                          />
                        </div>
                      )
                    })}
                    {loadingMore && (
                      <SpinnerLoader ariaLabel={'DropdownList'} style={{ margin: 12 }} size={24} />
                    )}
                  </div>
                </Scrollbar>
              </div>
            )}
            {!filterItems.length && !(isAllItemsSelectedInGroups && !searchLocal) && (
              <div
                className={styles.noResult}
                aria-label={getAriaLabel('DropdownList', 'noResult')}
              >
                {notFoundText}
              </div>
            )}
            {bottomActions && (
              <div
                className={styles.bottomActions}
                style={bottomActionsStyle}
                aria-label={getAriaLabel('DropdownList', 'Actions')}
              >
                {bottomActions}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
